import { paths } from "../../routes/paths";
import { convertToTitleCase, getDateWithFormat } from "../../utils/commons";
import { buildHeaderData, HEADER_COMBINATIONS } from "./headerDefinitions";

// Helper functions to reduce code duplication
export const formatValue = (value, defaultValue = "-") => value || defaultValue;

const getUiSchemaOptions = (propertyName, uiSchema) =>
  (uiSchema?.elements || [])
    .filter((element) => element?.scope === `#/properties/${propertyName}`)
    .flatMap((element) => element?.options?.childList || []);

export const getFilterOptionDisplayNames = (propertyName, ...filterConfigs) => {
  const options = filterConfigs.flatMap((filterConfig) => {
    const jsonSchema = filterConfig?.jsonSchema || filterConfig;
    const propertySchema = jsonSchema?.properties?.[propertyName];
    const schemaOptions =
      propertySchema?.items?.options || propertySchema?.options || [];

    return [...schemaOptions, ...getUiSchemaOptions(propertyName, filterConfig)];
  });

  return Object.fromEntries(
    options
      .filter(({ id, displayText }) => id && displayText)
      .map(({ id, displayText }) => [id, displayText])
  );
};

export const getActivityTypeDisplayNames = (...filterConfigs) =>
  getFilterOptionDisplayNames("engagementType", ...filterConfigs);

export const formatDate = (date, timezone, format) => {
  // Simply call getDateWithFormat which is mocked in tests
  return date ? getDateWithFormat(date, timezone, format) : "-";
};

export const getFormattedStatus = (status) => {
  if (!status) return { formatStatus: "", compositeStatusClass: "" };
  const formatStatus = status.replace("_", " ");
  const compositeStatusClass = status.toLowerCase().replace("_", "-");
  return { formatStatus, compositeStatusClass };
};

export const renderIdLink = (id, handleClick, item) => (
  <a
    className="performances-widget-id"
    href={`${paths.performances_list}/${paths.performance_details}/${id}`}
    rel="noopener noreferrer"
    onClick={(e) => handleClick(e, item)}
  >
    {id}
  </a>
);

export const getOutcomeName = (outcomes) =>
  outcomes?.map((outcome) => convertToTitleCase(outcome.outcome)).join(", ") || "-";

export const getOutcomeQuantity = (outcomes) =>
  outcomes?.map((outcome) => outcome.value ?? "N/A").join(", ") || "-";

export const renderStatusBadge = (status) => {
  if (!status) return null;
  const { formatStatus, compositeStatusClass } = getFormattedStatus(status);

  // Return the span element with the formatted status as children
  return (
    <span className={`performances-lifecycle-status ${compositeStatusClass}`}>
      {formatStatus}
    </span>
  );
};

export const getEngagementTypeSpecificTableData = (
  engagementType,
  data,
  handleClick,
  timezone
) => {
  // Handle invalid parameters
  if (!engagementType || !data || !Array.isArray(data)) {
    return undefined;
  }
  const typeSpecificTableData = {
    "package-scan": {
      headerData: buildHeaderData(HEADER_COMBINATIONS.packageScan),
      rowData: data.map((item) => ({
        id: renderIdLink(item.id, handleClick, item),
        externalId: formatValue(item.identifier),
        userId: formatValue(item.profileEmail),
        profileId: formatValue(item.profileId),
        channel: formatValue(item.channel),
        campaignName: formatValue(item.campaignName),
        performanceDate: formatDate(item.performanceDate, timezone),
        packageCode: formatValue(item?.metadata?.packageData?.package_code),
        outcomeName: getOutcomeName(item.outcomes),
        outcomeQuantity: getOutcomeQuantity(item.outcomes),
        activityType: formatValue(item.engagementType),
        sku: formatValue(item?.metadata?.packageData?.sku),
        freshByDate: formatDate(
          item?.metadata?.packageData?.freshness_date,
          timezone
        ),
        status: renderStatusBadge(item.status),
      })),
    },
    "code-scan-generic": {
      headerData: buildHeaderData(HEADER_COMBINATIONS.codeScan),
      rowData: data.map((item) => ({
        id: renderIdLink(item.id, handleClick, item),
        externalId: formatValue(item.identifier),
        userId: formatValue(item.profileEmail),
        profileId: formatValue(item.profileId),
        campaignName: formatValue(item.campaignName),
        performanceDate: formatDate(item.performanceDate, timezone),
        outcomeName: getOutcomeName(item.outcomes),
        outcomeQuantity: getOutcomeQuantity(item.outcomes),
        channel: formatValue(item.channel),
        status: renderStatusBadge(item.status),
      })),
    },
    "check-in": {
      headerData: buildHeaderData(HEADER_COMBINATIONS.checkIn),
      rowData: data.map((item) => ({
        id: renderIdLink(item.id, handleClick, item),
        referralIdentifier: formatValue(item.identifier),
        userProfileId: formatValue(item.profileId),
        campaignName: formatValue(item.campaignName),
        performanceDate: formatDate(item.performanceDate, timezone),
        outcomeName: getOutcomeName(item.outcomes),
        geolocation: `${item.metadata?.latitude || ""}, ${item.metadata?.longitude || ""}`,
        outcomeQuantity: getOutcomeQuantity(item.outcomes),
        status: renderStatusBadge(item.status),
      })),
    },
  };
  return typeSpecificTableData[engagementType];
};

export const performancesTableHeaderData = buildHeaderData(
  HEADER_COMBINATIONS.default,
  true
);
